import React, { Fragment, useContext, useEffect, useMemo } from "react";
import Axios from "axios";
import { AccountNav, Grid, Loader, Row, ViewContext } from "components/lib";
import "survey-core/defaultV2.min.css";
import { applyStackgoTheme } from "components/survey/stackgo-theme";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";

StylesManager.applyTheme("defaultV2");

// Mirrors the server's MODULE_REGISTRY (model/tranche2.js). Active modules carry a
// form selection; placeholder modules are listed as inactive and never posted.
const ACTIVE_MODULES = [
  { key: "consent", label: "Consent", legacyField: "consent_form_id" },
  { key: "intake", label: "Intake", legacyField: "intake_form_id" },
  {
    key: "risk_assessment",
    label: "Risk assessment",
    legacyField: "risk_assessment_form_id",
  },
];

const PLACEHOLDER_MODULES = [
  { key: "trigger_event_review", label: "Trigger-event review" },
];

// Server-controlled EDD block (data-model §settings). Input never sets unknown
// keys; capable_user_ids is validated server-side to be a subset of
// assessor_user_ids, and enabling EDD without both forms + >=1 capable assessor
// is rejected (FR-017).
const DEFAULT_EDD = {
  enabled: false,
  threshold: "high",
  client_form_id: null,
  assessment_form_id: null,
  capable_user_ids: [],
};

// Server-controlled periodic-review block (feature 005). Enabling requires a
// positive period in months for each rating, ordered high <= medium <= low
// (FR-002/FR-003); the server rejects anything else.
const DEFAULT_PERIODIC_REVIEW = {
  enabled: false,
  period_months: { high: null, medium: null, low: null },
};

// Server-controlled ongoing-monitoring block (feature 006). Independently
// enableable; the external provider drives the cadence, so the only setting is
// the enable flag.
const DEFAULT_ONGOING_MONITORING = {
  enabled: false,
};

// Public self-serve intake link (feature 008). The token is server-generated and
// read-only here; the client only toggles enable, the daily cap and the logo.
// Hand-mirrors DEFAULT_SETTINGS.public_form in model/tranche2.js — there is no
// shared constant, so keep the two in step.
const DEFAULT_PUBLIC_FORM = {
  enabled: false,
  token: null,
  daily_cap: 25,
  logo_url: null,
};

// Per-mode intake form overrides (feature 010). Each variant is an independent
// opt-in that names a DIFFERENT intake form for one route; while both are off every
// journey uses the Intake module form above. Hand-mirrors
// DEFAULT_SETTINGS.intake_overrides in model/tranche2.js — there is no shared
// constant, so keep the two in step.
//
// A factory, not a shared literal: each variant object is spread into its own
// hydration below, and a shared nested literal would alias between them.
const defaultIntakeOverrides = () => ({
  public: { enabled: false, form_id: null },
  direct: { enabled: false, form_id: null },
});

// Page-level copy for each variant. The server owns the LABELS (returned as
// `intake_variants`); these are the sentences that only make sense next to the
// Intake module dropdown, which the registry has no business carrying.
//
// `enableIf` is per-variant and deliberately uses enableIf rather than visibleIf:
// a DISABLED question keeps its value through SurveyJS's clear-invisible pass,
// a HIDDEN one does not. See docs/solutions/logic-errors/
// surveyjs-visibleif-gating-wipes-stored-settings-on-save.md
const INTAKE_VARIANT_UI = {
  public: {
    toggleTitle: "Use a different intake form for the public intake link",
    formTitle: "Public intake form",
    formDescription:
      "Used for every verification a client starts from your public intake link, where you hold no details for them yet.",
    enableIf: "{public_form_enabled} = true",
  },
  direct: {
    toggleTitle:
      "Use a different intake form for staff-initiated journeys",
    formTitle: "Staff-initiated intake form",
    formDescription:
      "Used when a staff member starts a verification by typing the client in by hand, and for scheduled re-verification of an existing client. Journeys prefilled from XPM or Pipedrive, and those started by a connected system, keep the Intake form above.",
    enableIf: undefined,
  },
};

const INTAKE_VARIANT_ORDER = ["public", "direct"];

// Server-controlled notification-recipient block (feature 010). Opt-in: while
// `enabled` is false every notification keeps its pre-existing recipients, so
// these per-type defaults only take effect once an admin turns it on. They mirror
// the server's NOTIFICATION_REGISTRY defaults — only the risk-assessment email
// went to account admins before this feature.
//
// Factories, not shared literals: each type's user_ids array is handed to its own
// SurveyJS checkbox as defaultValue, and SurveyJS mutates that array in place when
// the admin ticks a box. A shared array would leak selections between types.
const defaultNotificationType = (accountAdmins = false) => ({
  account_admins: accountAdmins,
  assessors: true,
  user_ids: [],
});

const defaultNotifications = () => ({
  enabled: false,
  types: {
    risk_assessment_ready: defaultNotificationType(true),
    edd_input_ready: defaultNotificationType(),
    review_due: defaultNotificationType(),
    reviews_digest: defaultNotificationType(),
    monitoring_match: defaultNotificationType(),
  },
});

const DEFAULT_SETTINGS = {
  enabled: false,
  assessor_user_ids: [],
  token_expiry_days: 7,
  pin_required_dashboard: true,
  pin_required_token_link: true,
  risk_mode: "manual_v1",
  modules: {
    consent: { status: "active", form_id: null, editable: true },
    intake: { status: "active", form_id: null, editable: true },
    risk_assessment: { status: "active", form_id: null, editable: true },
    edd: { status: "placeholder", form_id: null, editable: false },
    periodic_review: { status: "placeholder", form_id: null, editable: false },
    trigger_event_review: { status: "placeholder", form_id: null, editable: false },
  },
  consent_form_id: null,
  intake_form_id: null,
  risk_assessment_form_id: null,
  edd: DEFAULT_EDD,
  periodic_review: DEFAULT_PERIODIC_REVIEW,
  ongoing_monitoring: DEFAULT_ONGOING_MONITORING,
  public_form: DEFAULT_PUBLIC_FORM,
  intake_overrides: defaultIntakeOverrides(),
  notifications: defaultNotifications(),
};

// Ordered as the panel renders them. The risk-assessment alert leads because it
// is the highest-volume type and the one accounts most often want to narrow.
const NOTIFICATION_TYPE_ORDER = [
  "risk_assessment_ready",
  "edd_input_ready",
  "review_due",
  "reviews_digest",
  "monitoring_match",
];

function moduleFormId(settings, moduleDefinition) {
  return (
    settings.modules?.[moduleDefinition.key]?.form_id ||
    settings[moduleDefinition.legacyField] ||
    null
  );
}

export function Tranche2SettingsView() {
  const viewContext = useContext(ViewContext);
  const [loading, setLoading] = React.useState(true);
  const [settingsData, setSettingsData] = React.useState(null);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const response = await Axios.get("/api/integration/tranche2/settings");
        setSettingsData(response?.data?.data || null);
      } catch (err) {
        viewContext.handleError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, [viewContext]);

  const survey = useMemo(() => {
    if (!settingsData) return null;

    const settings = {
      ...DEFAULT_SETTINGS,
      ...(settingsData.settings || {}),
    };

    const assessorChoices = (settingsData.users || [])
      .filter((u) => u?.id)
      .map((u) => {
        const identity = u.name ? `${u.name} (${u.email})` : u.email;
        const status = u.disabled ? " - disabled" : "";

        return {
          value: u.id,
          text: `${identity} - ${u.permission}${status}`,
        };
      });

    const formChoices = (settingsData.forms || []).map((f) => ({
      value: f.id,
      text: `${f.name}${f.version ? ` (v${f.version})` : ""}`,
    }));

    const modulePanels = ACTIVE_MODULES.map((moduleDefinition) => ({
      type: "panel",
      name: `module_${moduleDefinition.key}`,
      title: `${moduleDefinition.label} module`,
      elements: [
        {
          type: "dropdown",
          name: `module_${moduleDefinition.key}_form_id`,
          title: `${moduleDefinition.label} form`,
          requiredIf: "{enabled} = true",
          choices: formChoices,
          defaultValue: moduleFormId(settings, moduleDefinition) || undefined,
        },
      ],
    }));

    // Two-level hydration, matching periodic_review.period_months and
    // notifications.types: a one-level spread would lose a variant the server
    // returned but the defaults do not name.
    const storedOverrides = settings.intake_overrides || {};
    const intakeOverrides = Object.fromEntries(
      INTAKE_VARIANT_ORDER.map((key) => [
        key,
        {
          ...defaultIntakeOverrides()[key],
          ...(storedOverrides[key] || {}),
        },
      ])
    );

    // Server-owned labels (feature 010), falling back to the local order so the
    // panel still renders against an older API response.
    const intakeVariants = (
      settingsData.intake_variants?.length
        ? settingsData.intake_variants
        : INTAKE_VARIANT_ORDER.map((key) => ({ key, label: key }))
    ).filter((variant) => variant?.key && INTAKE_VARIANT_UI[variant.key]);

    // Server-computed (feature 010). Rendered as a persistent panel element rather
    // than a post-save toast: onComplete finishes with a hard window.location
    // reload, so a toast would flash and vanish — and the divergence is a property
    // of the stored settings, so it should be visible every time the page opens.
    const intakeOverrideWarnings = settingsData.intake_override_warnings || [];
    const escapeHtml = (value) =>
      String(value).replace(
        /[&<>"']/g,
        (ch) =>
          ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch])
      );
    const divergenceHtml = intakeOverrideWarnings.length
      ? `<div style='padding:8px 12px;border:1px solid var(--sg-orange);background:var(--sg-orange-bg);border-radius:8px;color:var(--sg-orange);'><strong>These intake forms ask different questions.</strong><ul style='margin:6px 0 0 18px;'>${intakeOverrideWarnings
          .map((warning) => {
            const missing = (warning.only_in_baseline || [])
              .map((n) => `<code>${escapeHtml(n)}</code>`)
              .join(", ");
            const added = (warning.only_in_override || [])
              .map((n) => `<code>${escapeHtml(n)}</code>`)
              .join(", ");
            return `<li><strong>${escapeHtml(
              warning.label || warning.variant
            )}</strong>${
              missing ? ` &mdash; not asked on this route: ${missing}` : ""
            }${added ? ` &mdash; only on this route: ${added}` : ""}</li>`;
          })
          .join("")}</ul><div style='margin-top:6px;'>Fields your connected systems map from a question above will arrive blank for that route. This is only a warning &mdash; your settings saved.</div></div>`
      : null;

    const intakeOverridePanel = {
      type: "panel",
      name: "module_intake_overrides",
      title: "Intake form overrides",
      description:
        "Every journey uses the Intake form selected above unless you override it here. Turn an override on to use a different intake form for that route \u2014 for example a shorter form for clients whose details you already hold.",
      elements: [
        ...intakeVariants.flatMap((variant) => {
          const ui = INTAKE_VARIANT_UI[variant.key];
          return [
            {
              type: "boolean",
              name: `intake_override_${variant.key}_enabled`,
              title: ui.toggleTitle,
              defaultValue: intakeOverrides[variant.key].enabled,
              // enableIf, NOT visibleIf: a disabled question keeps its value
              // through doComplete()'s clearing pass; a hidden one is deleted from
              // sender.data before onComplete runs. Same choice, same reason, as
              // the public-form enable toggle.
              ...(ui.enableIf ? { enableIf: ui.enableIf } : {}),
            },
            {
              type: "dropdown",
              name: `intake_override_${variant.key}_form_id`,
              title: ui.formTitle,
              description: ui.formDescription,
              visibleIf: `{intake_override_${variant.key}_enabled} = true`,
              requiredIf: `{intake_override_${variant.key}_enabled} = true`,
              choices: formChoices,
              defaultValue: intakeOverrides[variant.key].form_id || undefined,
            },
          ];
        }),
        ...(divergenceHtml
          ? [
              {
                type: "html",
                name: "intake_override_divergence",
                html: divergenceHtml,
              },
            ]
          : []),
        {
          type: "html",
          name: "intake_override_mapping_warning",
          visibleIf: INTAKE_VARIANT_ORDER.map(
            (key) => `{intake_override_${key}_enabled} = true`
          ).join(" or "),
          html: "<div style='padding:8px 12px;border:1px solid var(--sg-orange);background:var(--sg-orange-bg);border-radius:8px;color:var(--sg-orange);'><strong>Question names matter.</strong> Your connected systems map fields by intake question <em>name</em>, not by its label. A question that exists in one intake form under a different name \u2014 or not at all \u2014 will arrive blank in XPM, Karbon, FYI and HubSpot from that route.</div>",
        },
      ],
    };

    const edd = {
      ...DEFAULT_EDD,
      ...(settings.edd || {}),
    };

    const eddPanel = {
      type: "panel",
      name: "module_edd",
      title: "Enhanced Due Diligence (EDD)",
      description:
        "Automatically engage high-risk customers in EDD. To enable, select both EDD forms and at least one EDD-capable assessor.",
      elements: [
        {
          type: "boolean",
          name: "edd_enabled",
          title: "Enable EDD",
          defaultValue: edd.enabled,
        },
        {
          type: "radiogroup",
          name: "edd_threshold",
          title: "EDD trigger threshold",
          defaultValue: edd.threshold,
          visibleIf: "{edd_enabled} = true",
          choices: [
            { value: "high", text: "High risk only" },
            { value: "high_medium", text: "High and Medium risk" },
          ],
        },
        {
          type: "dropdown",
          name: "edd_client_form_id",
          title: "Client-facing EDD form",
          description: "The form the customer completes when EDD is triggered.",
          visibleIf: "{edd_enabled} = true",
          requiredIf: "{edd_enabled} = true",
          choices: formChoices,
          defaultValue: edd.client_form_id || undefined,
        },
        {
          type: "dropdown",
          name: "edd_assessment_form_id",
          title: "Internal EDD assessment form",
          description: "The form an EDD-capable assessor completes to re-rate the customer.",
          visibleIf: "{edd_enabled} = true",
          requiredIf: "{edd_enabled} = true",
          choices: formChoices,
          defaultValue: edd.assessment_form_id || undefined,
        },
        {
          type: "checkbox",
          name: "edd_capable_user_ids",
          title: "EDD-capable assessors",
          description:
            "Only these assessors may complete EDD assessments and record officer approvals. Must be a subset of the assessors selected above.",
          visibleIf: "{edd_enabled} = true",
          choices: assessorChoices,
          defaultValue: edd.capable_user_ids,
          colCount: 1,
        },
      ],
    };

    const periodicReview = {
      ...DEFAULT_PERIODIC_REVIEW,
      ...(settings.periodic_review || {}),
      period_months: {
        ...DEFAULT_PERIODIC_REVIEW.period_months,
        ...((settings.periodic_review || {}).period_months || {}),
      },
    };

    const periodMonthsQuestion = (name, title) => ({
      type: "text",
      name,
      title,
      inputType: "number",
      visibleIf: "{periodic_review_enabled} = true",
      requiredIf: "{periodic_review_enabled} = true",
      validators: [
        { type: "numeric", minValue: 1, text: "Enter a positive number of months" },
      ],
    });

    const periodicReviewPanel = {
      type: "panel",
      name: "module_periodic_review",
      title: "Periodic reviews",
      description:
        "Re-review customers on a cadence set by their risk rating. To enable, set a review period in months for each rating, ordered so high ≤ medium ≤ low (higher risk is reviewed at least as often).",
      elements: [
        {
          type: "boolean",
          name: "periodic_review_enabled",
          title: "Enable periodic reviews",
          defaultValue: periodicReview.enabled,
        },
        periodMonthsQuestion("periodic_review_high_months", "High-risk review period (months)"),
        periodMonthsQuestion("periodic_review_medium_months", "Medium-risk review period (months)"),
        periodMonthsQuestion("periodic_review_low_months", "Low-risk review period (months)"),
      ],
    };

    const ongoingMonitoring = {
      ...DEFAULT_ONGOING_MONITORING,
      ...(settings.ongoing_monitoring || {}),
    };

    const ongoingMonitoringPanel = {
      type: "panel",
      name: "module_ongoing_monitoring",
      title: "Ongoing monitoring",
      description:
        "Keep completed customers under continuous PEP/Sanctions and adverse-media monitoring with the external provider. Customers are enrolled automatically when their risk assessment or EDD case completes, and surfaced on the Ongoing Monitoring list.",
      elements: [
        {
          type: "boolean",
          name: "ongoing_monitoring_enabled",
          title: "Enable ongoing monitoring",
          defaultValue: ongoingMonitoring.enabled,
        },
      ],
    };

    const publicForm = {
      ...DEFAULT_PUBLIC_FORM,
      ...(settings.public_form || {}),
    };

    const publicIntakeUrl = publicForm.token
      ? `${window.location.origin}/public/intake/${publicForm.token}`
      : null;

    const publicFormPanel = {
      type: "panel",
      name: "module_public_form",
      title: "Public intake link",
      description:
        "Publish a single, stable URL your clients can open to start verifying themselves — consent, intake, biometric and risk assessment. Requires the Tranche 2 flow enabled above and the consent, intake and risk-assessment forms selected.",
      elements: [
        {
          type: "boolean",
          name: "public_form_enabled",
          title: "Enable the public intake link",
          defaultValue: publicForm.enabled,
          enableIf:
            "{enabled} = true and {module_consent_form_id} notempty and {module_intake_form_id} notempty and {module_risk_assessment_form_id} notempty",
        },
        {
          type: "text",
          name: "public_form_daily_cap",
          title: "Daily limit (new verifications per day)",
          description:
            "Caps how many verifications the link can start each day, protecting against abuse.",
          inputType: "number",
          visibleIf: "{public_form_enabled} = true",
          defaultValue: publicForm.daily_cap,
          validators: [
            {
              type: "numeric",
              minValue: 1,
              maxValue: 500,
              text: "Enter a number between 1 and 500",
            },
          ],
        },
        {
          type: "text",
          inputType: "url",
          name: "public_form_logo_url",
          title: "Logo for the public page (optional)",
          description:
            "An https link to your logo, shown at the top of the public intake page. Leave blank to use the logo from your email settings.",
          maxLength: 500,
          visibleIf: "{public_form_enabled} = true",
          defaultValue: publicForm.logo_url || undefined,
          // inputType "url" accepts http:// — the regex is what enforces https.
          // Deliberately looser than the server's grammar: this is UX, the server
          // is authoritative, and chasing byte-parity between the two is a trap.
          validators: [
            {
              type: "regex",
              text: "Enter an https:// link to your logo image, or leave blank.",
              regex: "^https://[A-Za-z0-9-]+(\\.[A-Za-z0-9-]+)+(/.*)?$",
            },
          ],
        },
        {
          type: "html",
          name: "public_form_url",
          visibleIf: "{public_form_enabled} = true",
          html: publicIntakeUrl
            ? `<div style='padding:8px 12px;border:1px solid #d1d5db;background:#f9fafb;border-radius:8px;'><strong>Your public link:</strong><br/><code style='word-break:break-all;'>${publicIntakeUrl}</code><br/><span style='color:#6b7280;'>Share this URL with your clients. Disabling the link closes it immediately.</span></div>`
            : `<div style='padding:8px 12px;border:1px solid #d1d5db;background:#f9fafb;border-radius:8px;color:#6b7280;'>Save with the link enabled to generate your public URL.</div>`,
        },
      ],
    };

    const notifications = {
      ...defaultNotifications(),
      ...(settings.notifications || {}),
      types: {
        ...defaultNotifications().types,
        ...((settings.notifications || {}).types || {}),
      },
    };

    // Server-owned labels/descriptions; fall back to the key order so the panel
    // still renders against an older API response.
    const notificationTypes = (
      settingsData.notification_types?.length
        ? settingsData.notification_types
        : NOTIFICATION_TYPE_ORDER.map((key) => ({ key, label: key }))
    ).filter((t) => t?.key && notifications.types[t.key]);

    const notificationTypePanel = (definition) => {
      const config = {
        ...defaultNotificationType(),
        ...(notifications.types[definition.key] || {}),
      };
      const isEdd = definition.key === "edd_input_ready";

      return {
        type: "panel",
        name: `notification_${definition.key}`,
        title: definition.label,
        description: definition.description,
        visibleIf: "{notifications_enabled} = true",
        // Only the first stays open — five expanded sub-panels of three
        // questions each would bury the rest of this page.
        state: definition.key === "risk_assessment_ready" ? "expanded" : "collapsed",
        elements: [
          {
            type: "boolean",
            name: `notification_${definition.key}_account_admins`,
            title: "Notify account owners and admins",
            defaultValue: config.account_admins,
          },
          {
            type: "boolean",
            name: `notification_${definition.key}_assessors`,
            title: isEdd ? "Notify EDD-capable assessors" : "Notify assessors",
            defaultValue: config.assessors,
          },
          {
            type: "checkbox",
            name: `notification_${definition.key}_user_ids`,
            title: "Also notify these specific users",
            choices: assessorChoices,
            defaultValue: config.user_ids,
            colCount: 1,
          },
          ...(isEdd
            ? [
                {
                  type: "html",
                  name: `notification_${definition.key}_warning`,
                  html: "<div style='padding:8px 12px;border:1px solid var(--sg-orange);background:var(--sg-orange-bg);border-radius:8px;color:var(--sg-orange);'>Recipients who are not EDD-capable assessors will receive this alert but cannot open the case.</div>",
                },
              ]
            : []),
        ],
      };
    };

    const notificationsPanel = {
      type: "panel",
      name: "module_notifications",
      title: "Notification recipients",
      description:
        "Choose who receives each Tranche 2 staff notification. While this is off, every notification goes to its current default recipients.",
      elements: [
        {
          type: "boolean",
          name: "notifications_enabled",
          title: "Choose recipients for each notification",
          defaultValue: notifications.enabled,
        },
        {
          type: "html",
          name: "notifications_empty_notice",
          visibleIf: "{notifications_enabled} = true",
          html: "<div style='padding:8px 12px;border:1px solid #d1d5db;background:#f9fafb;border-radius:8px;color:#6b7280;'>A notification with no recipients selected is not sent to anyone. That is recorded in your logs, so the silence is deliberate and auditable.</div>",
        },
        ...notificationTypes.map(notificationTypePanel),
      ],
    };

    const placeholderList = PLACEHOLDER_MODULES.map(
      (moduleDefinition) => moduleDefinition.label
    ).join(", ");

    const surveyJSON = {
      showQuestionNumbers: "off",
      completeText: "Save Settings",
      showCompletedPage: false,
      pages: [
        {
          name: "tranche2_settings",
          elements: [
            {
              type: "html",
              name: "override_notice",
              html: "<div style='padding: 8px 12px; border: 1px solid var(--sg-orange); background: var(--sg-orange-bg); border-radius: 8px; margin-bottom: 12px;'><strong>Note:</strong> Enabling Tranche 2 overrides account-level PEP/Sanctions and adverse media behavior for this flow. After a positive biometric decision, PEP/Sanctions and adverse media checks run via Tranche 2 workflow.</div>",
            },
            {
              type: "boolean",
              name: "enabled",
              title: "Enable Tranche 2 direct mode",
              isRequired: true,
              defaultValue: settings.enabled,
            },
            {
              type: "checkbox",
              name: "assessor_user_ids",
              title: "Additional users who can complete risk assessments",
              description:
                "Owners and admins can always complete assessments. Select any extra users here.",
              choices: assessorChoices,
              defaultValue: settings.assessor_user_ids,
              colCount: 1,
            },
            {
              type: "text",
              name: "token_expiry_days",
              title: "Token expiry (days)",
              inputType: "number",
              isRequired: true,
              defaultValue: settings.token_expiry_days,
              validators: [
                {
                  type: "numeric",
                  minValue: 7,
                  maxValue: 30,
                  text: "Enter a number between 7 and 30",
                },
              ],
            },
            ...modulePanels,
            intakeOverridePanel,
            eddPanel,
            periodicReviewPanel,
            ongoingMonitoringPanel,
            publicFormPanel,
            notificationsPanel,
            {
              type: "html",
              name: "placeholder_modules",
              html: `<div style='padding: 8px 12px; border: 1px solid #d1d5db; background: #f9fafb; border-radius: 8px; margin-top: 12px; color: #6b7280;'><strong>Coming soon:</strong> ${placeholderList}. These modules are not yet active and cannot be configured.</div>`,
            },
          ],
        },
      ],
    };

    const model = new Model(surveyJSON);
    applyStackgoTheme(model);
    model.data = {
      enabled: settings.enabled,
      assessor_user_ids: settings.assessor_user_ids,
      token_expiry_days: settings.token_expiry_days,
      ...Object.fromEntries(
        ACTIVE_MODULES.map((moduleDefinition) => [
          `module_${moduleDefinition.key}_form_id`,
          moduleFormId(settings, moduleDefinition),
        ])
      ),
      edd_enabled: edd.enabled,
      edd_threshold: edd.threshold,
      edd_client_form_id: edd.client_form_id || undefined,
      edd_assessment_form_id: edd.assessment_form_id || undefined,
      edd_capable_user_ids: Array.isArray(edd.capable_user_ids)
        ? edd.capable_user_ids
        : [],
      periodic_review_enabled: periodicReview.enabled,
      periodic_review_high_months: periodicReview.period_months.high || undefined,
      periodic_review_medium_months: periodicReview.period_months.medium || undefined,
      periodic_review_low_months: periodicReview.period_months.low || undefined,
      ongoing_monitoring_enabled: ongoingMonitoring.enabled,
      public_form_enabled: publicForm.enabled,
      public_form_daily_cap: publicForm.daily_cap,
      public_form_logo_url: publicForm.logo_url || undefined,
      // SurveyJS data is flat, so each variant's two fields are seeded by key.
      ...Object.fromEntries(
        intakeVariants.flatMap((variant) => [
          [
            `intake_override_${variant.key}_enabled`,
            intakeOverrides[variant.key].enabled,
          ],
          [
            `intake_override_${variant.key}_form_id`,
            intakeOverrides[variant.key].form_id || undefined,
          ],
        ])
      ),
      notifications_enabled: notifications.enabled,
      // SurveyJS data is flat, so each type's three fields are seeded by key.
      ...Object.fromEntries(
        notificationTypes.flatMap((definition) => {
          const config = {
            ...defaultNotificationType(),
            ...(notifications.types[definition.key] || {}),
          };
          return [
            [
              `notification_${definition.key}_account_admins`,
              config.account_admins,
            ],
            [`notification_${definition.key}_assessors`, config.assessors],
            [
              `notification_${definition.key}_user_ids`,
              Array.isArray(config.user_ids) ? config.user_ids : [],
            ],
          ];
        })
      ),
    };

    model.onComplete.add(async (sender) => {
      try {
        const notificationsEnabled = sender.data.notifications_enabled === true;
        const publicFormEnabled = sender.data.public_form_enabled === true;
        const payload = {
          enabled: sender.data.enabled,
          assessor_user_ids: Array.isArray(sender.data.assessor_user_ids)
            ? sender.data.assessor_user_ids
            : [],
          token_expiry_days: Number.parseInt(sender.data.token_expiry_days, 10),
          pin_required_dashboard: true,
          pin_required_token_link: true,
          risk_mode: "manual_v1",
          modules: Object.fromEntries(
            ACTIVE_MODULES.map((moduleDefinition) => [
              moduleDefinition.key,
              {
                form_id:
                  sender.data[`module_${moduleDefinition.key}_form_id`] || null,
              },
            ])
          ),
          intake_overrides: Object.fromEntries(
            intakeVariants.map((variant) => {
              const enabled =
                sender.data[`intake_override_${variant.key}_enabled`] === true;
              const stored = intakeOverrides[variant.key];

              // SurveyJS clears invisible questions on complete
              // (clearInvisibleValues defaults to "onComplete"), so with an
              // override toggled off its form dropdown is ALREADY GONE from
              // sender.data. Posting a literal null there would silently forget
              // the form the admin had chosen — on a save they made for an
              // unrelated panel — and they would have to re-pick it every time
              // they toggled back on. Fall back to what is STORED, and read the
              // form only when the panel was actually visible.
              //
              // Explicit null (never undefined) on the visible branch:
              // mergeSettings is payload-wins-per-key, so an omitted key would
              // preserve the stored id and the admin could never clear it.
              //
              // The `enabled` flags are read straight from sender.data and that is
              // correct: both toggles are ungated or gated with enableIf, which
              // does NOT clear, so they always survive the clearing pass.
              // Same defect and same fix as the public_form block below.
              return [
                variant.key,
                {
                  enabled,
                  form_id: enabled
                    ? sender.data[`intake_override_${variant.key}_form_id`] || null
                    : stored.form_id ?? null,
                },
              ];
            })
          ),
          edd: {
            enabled: sender.data.edd_enabled === true,
            threshold:
              sender.data.edd_threshold === "high_medium"
                ? "high_medium"
                : "high",
            client_form_id: sender.data.edd_client_form_id || null,
            assessment_form_id: sender.data.edd_assessment_form_id || null,
            capable_user_ids: Array.isArray(sender.data.edd_capable_user_ids)
              ? sender.data.edd_capable_user_ids
              : [],
          },
          periodic_review: {
            enabled: sender.data.periodic_review_enabled === true,
            period_months: {
              high: Number.parseInt(sender.data.periodic_review_high_months, 10) || null,
              medium: Number.parseInt(sender.data.periodic_review_medium_months, 10) || null,
              low: Number.parseInt(sender.data.periodic_review_low_months, 10) || null,
            },
          },
          ongoing_monitoring: {
            enabled: sender.data.ongoing_monitoring_enabled === true,
          },
          public_form: {
            enabled: publicFormEnabled,
            // SurveyJS clears invisible questions on complete
            // (clearInvisibleValues defaults to "onComplete"), so with the link
            // toggled off BOTH fields below are already gone from sender.data.
            // Falling back to a literal would post 25 over a customised cap and
            // null over a configured logo — silently discarding settings the admin
            // never touched, on a save they made for an unrelated panel. Fall back
            // to what is STORED, and read the form only when the panel was visible.
            // Same defect and same fix as the notification types below.
            daily_cap: publicFormEnabled
              ? Number.parseInt(sender.data.public_form_daily_cap, 10) ||
                publicForm.daily_cap
              : publicForm.daily_cap,
            // Explicit null rather than undefined: mergeSettings is
            // payload-wins-per-key, so omitting the key would preserve the stored
            // logo and the admin could never clear it.
            logo_url: publicFormEnabled
              ? (sender.data.public_form_logo_url || "").trim() || null
              : publicForm.logo_url ?? null,
          },
          notifications: {
            enabled: notificationsEnabled,
            // SurveyJS clears invisible questions on complete
            // (clearInvisibleValues defaults to "onComplete"), so when the master
            // toggle is off every per-type value is already gone from sender.data.
            // Reading it then would post all-false over the admin's stored choices
            // and silently switch every notification off the next time they turn
            // the feature back on. Keep what is stored instead, and read the form
            // only when the panels were actually visible.
            types: Object.fromEntries(
              notificationTypes.map((definition) => {
                const stored = {
                  ...defaultNotificationType(),
                  ...(notifications.types[definition.key] || {}),
                };
                if (!notificationsEnabled) return [definition.key, stored];

                const namedIds =
                  sender.data[`notification_${definition.key}_user_ids`];

                return [
                  definition.key,
                  {
                    account_admins:
                      sender.data[
                        `notification_${definition.key}_account_admins`
                      ] === true,
                    assessors:
                      sender.data[
                        `notification_${definition.key}_assessors`
                      ] === true,
                    // A fully-unticked checkbox is removed from the data rather
                    // than left as []; while the panel is visible that is the
                    // admin deliberately naming nobody.
                    user_ids: Array.isArray(namedIds) ? namedIds : [],
                  },
                ];
              })
            ),
          },
        };

        await Axios.post("/api/integration/tranche2/settings", payload);

        viewContext.notification.show(
          "Tranche 2 settings saved",
          "success",
          true,
          "toast",
          "check"
        );
        window.location = "/account/tranche2-settings";
      } catch (err) {
        // The server rejects enabling EDD without both forms + >=1 capable
        // assessor (FR-017), and enabling periodic reviews without three positive
        // periods ordered high <= medium <= low (FR-002/FR-003), among other
        // validation; surface that message and re-open the form so the admin can
        // correct their selections and retry.
        viewContext.handleError(err);
        sender.clear(false, false);
        sender.currentPageNo = 0;
      }
    });

    return model;
  }, [settingsData, viewContext]);

  if (loading) return <Loader />;
  if (!survey) return <Loader />;

  return (
    <Fragment>
      <AccountNav />
      <Grid>
        <Row>
          <h1>Tranche 2 Settings</h1>
          <Survey model={survey} />
        </Row>
      </Grid>
    </Fragment>
  );
}
